/* eslint-disable max-lines-per-function */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentUser } from 'src/models/lmsConfig/queries';
import axios from 'src/workspace/axios';
import { setRouteModuleBreadCrumbParams } from 'src/workspace/slice';
import {
  getAppSearchParams,
  getModuleProps,
  isLmsWeb,
} from 'src/workspace/utils';
import {
  clearPerformanceState,
  setKeyMetricsBreakdownGraph,
  setKeyMetricsDetails,
  setKeyMetricsTeamBreakdown,
  setKeyMetricsTrendGraph,
  setStatus,
} from '../slice';
import { fetchUserMetric } from '../thunk';
import { genericParams, teamBreakdownColumns } from './constants';

const fetchBreakdownGraph = createAsyncThunk(
  'breakdownGraph',
  async ({ id, card, userId }: any, { dispatch }) => {
    try {
      dispatch(setStatus({ key_metrics_breakdown_graph: 'loading' }));
      const { data } = await axios.get(
        '/performance/v1/users/metrics/breakdown/graph',
        {
          params: {
            goal_definition_code: id,
            filters: JSON.stringify({
              user_id: userId,
              frequency: card?.frequency,
            }),
            drilldown_meta: JSON.stringify({
              user_id: userId,
            }),
          },
        },
      );
      dispatch(setKeyMetricsBreakdownGraph(data));
      dispatch(setStatus({ key_metrics_breakdown_graph: 'done' }));
    } catch (e) {
      dispatch(setStatus({ key_metrics_breakdown_graph: 'error' }));
    }
  },
);

const fetchTrendGraph = createAsyncThunk(
  'trendGraph',
  async ({ id, card, userId }: any, { dispatch }) => {
    try {
      dispatch(setStatus({ key_metrics_trend_graph: 'loading' }));
      const { data } = await axios.get(
        '/performance/v1/users/metrics/trend/graph',
        {
          params: {
            goal_definition_code: id,
            filters: JSON.stringify({
              user_id: userId,
              frequency: card?.frequency,
            }),
            drilldown_meta: JSON.stringify({
              user_id: userId,
            }),
          },
        },
      );
      dispatch(setKeyMetricsTrendGraph(data));
      dispatch(setStatus({ key_metrics_trend_graph: 'done' }));
    } catch (e) {
      dispatch(setStatus({ key_metrics_trend_graph: 'error' }));
    }
  },
);

const fetchAchievedBy = async (params) => {
  const { data } = await axios.get('/performance/v1/users/metrics/achievedby', {
    params,
  });
  return data?.body;
};

export const fetchKeyMetricsDetails = createAsyncThunk(
  'keyMetrics',
  async ({ id, card, userId, filters = {} }: any, { dispatch }) => {
    try {
      dispatch(fetchBreakdownGraph({ id, card, userId }));
      dispatch(fetchTrendGraph({ id, card, userId }));

      dispatch(setStatus({ key_metrics_details: 'loading' }));
      dispatch(setStatus({ key_metrics_team_breakdown: 'loading' }));

      const data: any = await fetchUserMetric({
        goalDefinitionCode: id,
        userId,
        filters: genericParams(userId, card, id, filters).filters,
        meta: {
          user_id: userId,
        },
        drilldownGroupBy: {
          type: 'REGION',
          regionType: 'USER',
        },
      });

      const achievedBy = await fetchAchievedBy(
        genericParams(userId, card, id, filters),
      );

      const groupedUsersSet = data?.body?.result?.grouped_record_sets || [];

      dispatch(
        setKeyMetricsDetails({
          ...(data?.body || {}),
          card,
          achievedBy,
        }),
      );

      try {
        const groupedUsersSetWithAchievedBy = await Promise.all(
          groupedUsersSet.map(async (groupedUser) => {
            const groupedUserId = groupedUser?.group_by_key?.code;

            const { subordinatesCount } =
              getModuleProps?.()?.getAllUsers?.()?.[groupedUserId] || {};

            try {
              const groupedUserAchievedBy = await fetchAchievedBy(
                genericParams(groupedUserId, card, id, filters),
              );

              return {
                body: {
                  result: {
                    ...groupedUser,
                    achievedBy: groupedUserAchievedBy,
                    subordinatesCount,
                  },
                  progress_type_settings: data?.body?.progress_type_settings,
                },
              };
            } catch (e) {
              return {
                body: {
                  result: {
                    ...groupedUser,
                    subordinatesCount,
                  },
                  progress_type_settings: data?.body?.progress_type_settings,
                },
              };
            }
          }),
        );
        dispatch(
          setKeyMetricsTeamBreakdown({
            data: groupedUsersSetWithAchievedBy,
            columns: teamBreakdownColumns,
          }),
        );
        dispatch(setStatus({ key_metrics_team_breakdown: 'done' }));
      } catch (e) {
        dispatch(setStatus({ key_metrics_team_breakdown: 'error' }));
      }

      dispatch(setStatus({ key_metrics_details: 'done' }));
    } catch (e) {
      dispatch(setStatus({ key_metrics_details: 'error' }));
      dispatch(setStatus({ key_metrics_team_breakdown: 'error' }));
    }
  },
);

const onUpdate = async ({ dispatch, props }) => {
  dispatch(clearPerformanceState());
  const { id } = props;
  let { card } = props;
  if (!card) {
    card = getAppSearchParams() || {};
  }

  if (isLmsWeb()) {
    dispatch(
      setRouteModuleBreadCrumbParams({ keyMetricName: card.title || '' }),
    );
  }

  const { userId: propsUserId } = card || {};
  const userId = propsUserId || getCurrentUser()?.code;
  dispatch(fetchKeyMetricsDetails({ id, card, userId }));
};

const onActivate = () => {};

const onDeactivate = () => {};

export default { onActivate, onDeactivate, onUpdate };
